import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PriorityBadge, StatusBadge } from "@/components/Badges";
import { usePriorities } from "@/context/PrioritiesContext";
import { AttachmentChip } from "@/components/Attachments";
import {
  ArrowLeft, BellRing, Ban, CheckCircle2, RotateCcw, Info, Link2, Mail, Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import SendReminderModal from "@/components/SendReminderModal";
import CancelTicketModal from "@/components/CancelTicketModal";
import ResolveTicketModal from "@/components/ResolveTicketModal";
import TicketCustomFields from "@/components/TicketCustomFields";
import SendWaTemplateDialog from "@/components/SendWaTemplateDialog";
import SendEmailDialog from "@/components/SendEmailDialog";
import { ticketTypeMeta } from "@/lib/ticketType";
import CustomerStickyNotes from "@/components/customers/CustomerStickyNotes";
import CustomerHistoryPanel from "@/components/CustomerHistoryPanel";
import ReturnToDepartmentModal from "@/components/ReturnToDepartmentModal";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ConversationThread } from "@/components/tickets/ConversationThread";
import { TicketSidebar, ALL_STATUSES } from "@/components/tickets/TicketSidebar";

// Status transition matrix — which statuses can move to which next.
const ALLOWED_NEXT = {
  open: ["in_progress", "pending_user", "resolved", "closed", "cancelled"],
  in_progress: ["open", "pending_user", "resolved", "closed", "cancelled"],
  pending_user: ["open", "in_progress", "resolved", "closed", "cancelled"],
  pending_department: ["in_progress", "resolved", "closed", "cancelled"],
  resolved: ["in_progress", "closed"],
  closed: ["in_progress"],
  cancelled: ["in_progress"],
};

const _fmtMins = (m) => (!m ? "—" : m < 60 ? `${m}m` : (m % 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m / 60}h`));

function SlaChip({ label, mins, breached, remaining, done, testid }) {
  if (!mins) return null;
  const tone = breached ? "bg-rose-50 text-rose-700 border-rose-200"
    : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  const status = breached ? "Breached" : done ? "Met" : (remaining != null ? `${_fmtMins(Math.abs(remaining))} left` : "On track");
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] ${tone}`} data-testid={testid}>
      <span className="font-medium">{label}</span>
      <span className="opacity-70">{_fmtMins(mins)}</span>
      <span className="font-semibold">· {status}</span>
    </span>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const { priorityOptions: PRIORITY_OPTIONS } = usePriorities();
  const [ticket, setTicket] = useState(null);
  const [agents, setAgents] = useState([]);
  const [comment, setComment] = useState("");
  const [commentAttach, setCommentAttach] = useState([]);
  const [sending, setSending] = useState(false);

  // Modals
  const [reminderOpen, setReminderOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [resolveTarget, setResolveTarget] = useState("resolved");
  const [sendToWhatsapp, setSendToWhatsapp] = useState(true);
  const [waTemplateOpen, setWaTemplateOpen] = useState(false);
  const [waWindow, setWaWindow] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [returnDeptOpen, setReturnDeptOpen] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then((r) => setTicket(r.data));

  useEffect(() => {
    load();
    api.get("/users").then((r) => setAgents(r.data.filter((u) => u.active)));
  }, [id]);

  // Whenever the ticket reloads, re-fetch the WhatsApp 24h-window status
  useEffect(() => {
    if (!ticket?.id) return;
    if ((ticket.channel || "").toLowerCase() !== "whatsapp") { setWaWindow(null); return; }
    api.get(`/tickets/${ticket.id}/wa-window`).then((r) => setWaWindow(r.data)).catch(() => setWaWindow(null));
  }, [ticket?.id, ticket?.channel, ticket?.comments?.length]);

  const update = async (patch) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, patch);
      setTicket(data);
      toast.success("Ticket updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const STATUS_PERM = {
    in_progress: "tickets.mark_in_progress",
    pending_user: "tickets.mark_pending_department",
    resolved: "tickets.mark_resolved",
    closed: "tickets.close",
    cancelled: "tickets.cancel",
  };

  const canSetStatus = (next) => {
    if (!hasPerm("change_status")) return false;
    if (STATUS_PERM[next] && !hasPerm(STATUS_PERM[next])) return false;
    if (["resolved", "closed", "cancelled"].includes(ticket?.status)
        && !["resolved", "closed", "cancelled"].includes(next)
        && !hasPerm("reopen_ticket")) return false;
    return true;
  };

  const handleStatusChange = (next) => {
    if (!ticket || next === ticket.status) return;
    if (!hasPerm("change_status")) return toast.error("You don't have permission to change status.");
    if (STATUS_PERM[next] && !hasPerm(STATUS_PERM[next])) {
      return toast.error("You do not have permission to perform this action.");
    }
    // Route through modals for status changes that need a reason
    if (next === "cancelled") {
      setCancelOpen(true);
      return;
    }
    if (next === "resolved" || next === "closed") {
      setResolveTarget(next);
      setResolveOpen(true);
      return;
    }
    // Reopen check
    if (["resolved", "closed", "cancelled"].includes(ticket.status) && !["resolved", "closed", "cancelled"].includes(next)) {
      if (!hasPerm("reopen_ticket")) return toast.error("You don't have permission to reopen.");
    }
    update({ status: next });
  };

  const submitComment = async () => {
    if (!comment.trim() && commentAttach.length === 0) return;
    if (!hasPerm("comment_ticket")) return toast.error("You don't have permission to comment.");
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${id}/comments`, {
        body: comment, internal: false, attachment_ids: commentAttach,
        send_to_whatsapp: sendToWhatsapp,
      });
      setTicket(data);
      setComment("");
      setCommentAttach([]);
    } catch (e) { toast.error("Failed"); }
    finally { setSending(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this ticket permanently?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket deleted");
      navigate("/tickets");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot delete");
    }
  };

  const resendResolutionEmail = async () => {
    if (!window.confirm("Re-send the resolution email (with a fresh 7-day magic-link) to the department POCs?")) return;
    try {
      const { data } = await api.post(`/tickets/${id}/resend-resolution-email`);
      if (data.sent) {
        toast.success(`Resolution email re-sent to ${data.to?.length || 0} POC${data.to?.length === 1 ? "" : "s"} · 7-day link`);
        load();
      } else {
        toast.error(data.error || "Re-send failed — check email log");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not re-send");
    }
  };

  if (!ticket) return <div className="text-slate-400 text-sm" data-testid="ticket-detail-loading">Loading...</div>;

  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  const canDelete = hasPerm("delete_ticket");
  const canReminder = hasPerm("send_reminder") && !["resolved", "closed", "cancelled"].includes(ticket.status);
  const canCancel = hasPerm("cancel_ticket") && ticket.status !== "cancelled";
  const canClose = hasPerm("close_ticket") && !["resolved", "closed", "cancelled"].includes(ticket.status);
  const canReturnToDept = hasPerm("tickets.bounce_to_department") && ticket.status === "resolved";
  // Resolved tickets are bounced back via "Return to Department" instead of a generic reopen.
  const canReopen = hasPerm("reopen_ticket") && ["closed", "cancelled"].includes(ticket.status);
  const canResendResolution = hasPerm("send_reminder") && !!ticket.department_id && !["resolved", "closed", "cancelled"].includes(ticket.status);
  const canViewAssignee = hasPerm("tickets.view_assignee");
  const canAssign = hasPerm("tickets.assign") || hasPerm("tickets.reassign");
  const canChangeStatus = hasPerm("change_status");
  const canSendEmail = hasPerm("tickets.send_manual_email");

  const allowedStatuses = ALLOWED_NEXT[ticket.status] || [];

  return (
    <div className="space-y-5" data-testid="ticket-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700"
        data-testid="ticket-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      {ticket.customer_id && <CustomerStickyNotes customerId={ticket.customer_id} />}

      {ticket.rework_pending && (
        <div
          className="rounded-lg border border-orange-300 bg-orange-50 px-4 py-3 flex items-start gap-3"
          data-testid="ticket-rework-banner"
        >
          <Undo2 className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-orange-900">
              Returned to department for rework
            </div>
            <div className="text-sm text-orange-800 mt-0.5 whitespace-pre-wrap">
              {ticket.rework_reason}
            </div>
            <div className="text-xs text-orange-600 mt-1.5">
              Requested by {ticket.rework_requested_by_name || "an agent"}
              {ticket.rework_requested_at ? ` · ${fmt(ticket.rework_requested_at)}` : ""}
              {ticket.rework_count > 1 ? ` · rework #${ticket.rework_count}` : ""}
              {" — awaiting an updated resolution from the department."}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className={`border-slate-200 ${ticketTypeMeta(ticket.ticket_type)?.leftBar || ""} ${ticketTypeMeta(ticket.ticket_type) ? "border-l-4" : ""}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500" data-testid="ticket-code">{ticket.code}</span>
                    {(() => {
                      const tm = ticketTypeMeta(ticket.ticket_type);
                      if (!tm) return null;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${tm.pill}`}
                              data-testid="ticket-type-chip">
                          <tm.icon className="w-3 h-3" strokeWidth={2.2} />
                          {tm.label}
                          {tm.pulse && <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse ml-0.5" />}
                        </span>
                      );
                    })()}
                  </div>
                  <CardTitle className="font-heading text-2xl mt-1" data-testid="ticket-subject">{ticket.subject}</CardTitle>
                  <div className="text-sm text-slate-500 mt-2">
                    {ticket.customer_name} · via {ticket.channel} · {fmt(ticket.created_at)}
                    {ticket.source && ticket.source !== "manual" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
                        {ticket.source.replace("_", " ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PriorityBadge value={ticket.priority} />
                  <StatusBadge value={ticket.status} />
                </div>
              </div>
              {/* Resolution / cancellation banner */}
              {(ticket.closure_reason_name || ticket.cancel_reason_name) && (
                <div className={`mt-3 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${
                  ticket.cancel_reason_name
                    ? "bg-rose-50 border-rose-100 text-rose-800"
                    : "bg-emerald-50 border-emerald-100 text-emerald-800"
                }`}>
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold">
                      {ticket.cancel_reason_name ? "Cancelled: " : "Closed: "}
                    </span>
                    {ticket.cancel_reason_name || ticket.closure_reason_name}
                  </div>
                </div>
              )}
              {ticket.reminder_count > 0 && (
                <div className="mt-2 text-[11px] text-amber-700 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded px-2 py-0.5">
                  <BellRing className="w-3 h-3" />
                  {ticket.reminder_count} reminder{ticket.reminder_count === 1 ? "" : "s"} sent
                </div>
              )}
              {ticket.sla && (ticket.sla.first_response_minutes || ticket.sla.resolution_minutes) && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="ticket-sla-strip">
                  <SlaChip label="First response" mins={ticket.sla.first_response_minutes}
                    breached={ticket.sla.first_response_breached}
                    remaining={ticket.sla.first_response_remaining_minutes}
                    done={!!ticket.first_response_at} testid="sla-first-response" />
                  <SlaChip label="Resolution" mins={ticket.sla.resolution_minutes}
                    breached={ticket.sla.resolution_breached}
                    remaining={ticket.sla.resolution_remaining_minutes}
                    done={["resolved", "closed"].includes(ticket.status)} testid="sla-resolution" />
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ticket-description">
                {ticket.description || <span className="text-slate-400">No description provided.</span>}
              </div>
              {(ticket.attachment_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                  {ticket.attachment_ids.map((fid) => (
                    <AttachmentChip key={fid} fileId={fid} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <TicketCustomFields ticket={ticket} onUpdated={load} />

          {/* Action toolbar */}
          <div className="flex flex-wrap items-center gap-2" data-testid="ticket-actions">
            {canReminder && (
              <Button
                variant="outline" size="sm"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => setReminderOpen(true)}
                data-testid="ticket-reminder-btn"
              >
                <BellRing className="w-3.5 h-3.5 mr-1.5" /> Send reminder
              </Button>
            )}
            {canSendEmail && !["cancelled"].includes(ticket.status) && (
              <Button
                variant="outline" size="sm"
                className="border-violet-200 text-violet-700 hover:bg-violet-50"
                onClick={() => setEmailDialogOpen(true)}
                data-testid="ticket-send-email-btn"
              >
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Send email
              </Button>
            )}
            {canResendResolution && (
              <Button
                variant="outline" size="sm"
                className="border-sky-200 text-sky-700 hover:bg-sky-50"
                onClick={resendResolutionEmail}
                data-testid="ticket-resend-resolution-btn"
                title="Re-send the resolution email with a fresh 7-day magic-link"
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Resend resolution email
              </Button>
            )}
            {canClose && (
              <Button
                variant="outline" size="sm"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => { setResolveTarget("resolved"); setResolveOpen(true); }}
                data-testid="ticket-resolve-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resolve
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline" size="sm"
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
                onClick={() => setCancelOpen(true)}
                data-testid="ticket-cancel-btn"
              >
                <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
            )}
            {canReturnToDept && (
              <Button
                variant="outline" size="sm"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => setReturnDeptOpen(true)}
                data-testid="ticket-return-dept-btn"
              >
                <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Return to Department
              </Button>
            )}
            {canReopen && (
              <Button
                variant="outline" size="sm"
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                onClick={() => update({ status: "in_progress" })}
                data-testid="ticket-reopen-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reopen
              </Button>
            )}
          </div>

          <ConversationThread
            ticket={ticket}
            canComment={hasPerm("comment_ticket")}
            canSendTemplate={hasPerm("send_reminder")}
            comment={comment}
            setComment={setComment}
            commentAttach={commentAttach}
            setCommentAttach={setCommentAttach}
            sending={sending}
            onSubmit={submitComment}
            sendToWhatsapp={sendToWhatsapp}
            setSendToWhatsapp={setSendToWhatsapp}
            waWindow={waWindow}
            onOpenWaTemplate={() => setWaTemplateOpen(true)}
          />
        </div>

        <TicketSidebar
          ticket={ticket}
          agents={agents}
          user={user}
          priorityOptions={PRIORITY_OPTIONS}
          allowedStatuses={allowedStatuses}
          canSetStatus={canSetStatus}
          canChangeStatus={canChangeStatus}
          canViewAssignee={canViewAssignee}
          canAssign={canAssign}
          canDelete={canDelete}
          onStatusChange={handleStatusChange}
          onUpdate={update}
          onDelete={remove}
          onOpenHistory={() => setHistOpen(true)}
          onInsertKbLink={(md) => setComment((c) => (c ? `${c}\n\n${md}` : md))}
        />
      </div>

      <SendReminderModal
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        ticket={ticket}
        onSent={setTicket}
      />
      <CancelTicketModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        ticket={ticket}
        onCancelled={setTicket}
      />
      <ResolveTicketModal
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        ticket={ticket}
        targetStatus={resolveTarget}
        onResolved={setTicket}
      />
      <SendWaTemplateDialog
        open={waTemplateOpen}
        onOpenChange={setWaTemplateOpen}
        ticket={ticket}
        onSent={setTicket}
      />
      <SendEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        ticket={ticket}
        onSent={load}
      />
      <ReturnToDepartmentModal
        open={returnDeptOpen}
        onOpenChange={setReturnDeptOpen}
        ticket={ticket}
        onDone={(d) => { if (d?.ticket) setTicket(d.ticket); else load(); }}
      />

      <Sheet open={histOpen} onOpenChange={setHistOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0" data-testid="td-customer-history-sheet">
          {histOpen && (
            <CustomerHistoryPanel
              countryCode={ticket.country_code}
              contactNumber={ticket.contact_number}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
